import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  StateReportModel,
  StateReportDocument,
} from "../../models/StateReportModel";
import { apiClient } from "../../helper/api";
import useAuth from "../../context/AuthProvider";
import { formatUtcToLocal } from "../../helper/utils";
import { loadRemoteDocumentForViewer } from "../../services/api/documentViewerService";
import { StateReportDocumentViewerModal } from "./StateReportDocumentViewerModal";
import Swal from "sweetalert2";
import moment from "moment";
import { GlobalStyles } from "@mui/material";
import {
  getClientFacingAdditionalNotes,
  getStateReportPreviewTitle,
} from "../../helper/noClientActionRequired";
import {
  stripUrlsFromInstructions,
  getNoticeSummaryItems,
  getNoticePaymentItems,
  getNoticeStatusItems,
  formatNoticeTaxAmount,
  NoticeReportSummaryCard,
  NoticeFilingDetailsCard,
  NoticeInstructionsAndNotesCard,
  NoticeStatusCard,
  noticeSectionTitleSx,
  noticeScreenCardSx,
} from "./stateReportNoticeLayout";

interface StateReportPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  stateReport: StateReportModel | null;
  onMarkAsDone?: () => void | Promise<void>;
  selectedTab?: number;
  states?: { stateId: number; stateName: string; postalAbbv?: string }[];
}

export const StateReportPreviewDialog: React.FC<
  StateReportPreviewDialogProps
> = ({
  open,
  onClose,
  stateReport,
  onMarkAsDone,
  selectedTab = 1,
  states = [],
}) => {
  const [documents, setDocuments] = useState<StateReportDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(
    null,
  );
  const [documentViewerIsBlobUrl, setDocumentViewerIsBlobUrl] = useState(false);
  const [documentViewerFileName, setDocumentViewerFileName] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const token = user?.token;
  const dialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && stateReport?.stateReportId) {
      fetchDocuments();
    } else {
      setDocuments([]);
    }
  }, [open, stateReport?.stateReportId]);

  const fetchDocuments = async () => {
    if (!stateReport?.stateReportId) return;

    setLoadingDocuments(true);
    if(stateReport?.documents && stateReport?.documents?.length > 0){
      setDocuments(stateReport?.documents);
      setLoadingDocuments(false);
      return;
    }
    try {
      const res = await apiClient(
        `/StateReport/getStateReportDocuments?stateReportId=${stateReport.stateReportId}`,
        {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
        },
      );

      if (res && res.status && res.data) {
        setDocuments(res.data);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewDocument = async (blobAddress: string, fileName?: string) => {
    if (!blobAddress) return;

    setLoading(true);
    setDocumentViewerOpen(true);
    setDocumentViewerFileName(fileName || "document");
    setDocumentViewerUrl(null);
    setDocumentViewerIsBlobUrl(false);

    try {
      const result = await loadRemoteDocumentForViewer(blobAddress, fileName);
      setDocumentViewerUrl(result.viewerUrl);
      setDocumentViewerIsBlobUrl(result.isBlobUrl);
    } catch (error) {
      console.error("Error loading document:", error);
      setDocumentViewerOpen(false);

      Swal.fire({
        title: "Error",
        text: "Failed to load file. Opening in new tab instead.",
        icon: "warning",
        confirmButtonText: "OK",
      }).then(() => {
        window.open(blobAddress, "_blank");
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDocumentViewer = () => {
    if (documentViewerUrl && documentViewerIsBlobUrl) {
      URL.revokeObjectURL(documentViewerUrl);
    }
    setDocumentViewerOpen(false);
    setDocumentViewerUrl(null);
    setDocumentViewerIsBlobUrl(false);
    setDocumentViewerFileName(null);
  };
  const handleDownloadDocument = (doc: StateReportDocument) => {
    if (doc.blobAddress) {
      const link = document.createElement("a");
      link.href = doc.blobAddress;
      link.download = doc.documentName || "document.pdf";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      // Prepare documents array with documentName and blobAddress
      const documentsPayload = documents
        .filter((doc) => doc.documentName && doc.blobAddress)
        .map((doc) => ({
          documentName: doc.documentName || "",
          blobAddress: doc.blobAddress || "",
        }));

      if (documentsPayload.length === 0) {
        Swal.fire({
          title: "Error",
          text: "No valid documents to download.",
          icon: "error",
          confirmButtonText: "OK",
        });
        setDownloadingAll(false);
        return;
      }

      const response = await apiClient(
        "/StateReport/generateComprehensiveStateReportDocumentsZip",
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            documents: documentsPayload,
          }),
        },
      );

      if (response?.status && response?.data) {
        // Handle different response types
        if (response.data.zip_url) {
          // Direct URL download
          window.open(response.data.zip_url, "_blank");
        } else if (response.data.zipBase64) {
          // Base64 encoded ZIP
          const byteCharacters = atob(response.data.zipBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/zip" });

          const fileName = `StateReportDocuments_${stateReport?.stateReportId}_${new Date().toISOString().split("T")[0]}.zip`;
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          Swal.fire({
            title: "Warning",
            text:
              response?.message ||
              "Download initiated, but no file data received.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          return;
        }

        Swal.fire({
          title: "Success!",
          text: response?.message || "All documents downloaded successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        throw new Error(response?.message || "Failed to generate ZIP file.");
      }
    } catch (error: any) {
      console.error("Error downloading all documents:", error);
      Swal.fire({
        title: "Error",
        text:
          error?.message || "Failed to download documents. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleMarkAsDone = () => {
    if (!stateReport?.stateReportId) return;

    const isCompletedTab = selectedTab === 2;
    const actionText = isCompletedTab
      ? "Return to Outstanding"
      : "mark as Completed";
    const statusText = isCompletedTab ? "Revised" : "Completed";
    const successText = isCompletedTab
      ? "The report has been returned to Outstanding."
      : "The report has been marked as Completed.";

    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${actionText}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const res = await apiClient(`/StateReport/UpdateClientStateReportStatus`, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: stateReport.stateReportId,
            status: statusText,
          }),
        });

        if (!res) {
          await Swal.fire({
            title: "Error!",
            text: `Failed to ${actionText}.`,
            icon: "error",
          });
          return;
        }

        if (res.status) {
          if (onMarkAsDone) {
            await onMarkAsDone();
          }
          onClose();
          await Swal.fire({
            title: "Success!",
            text: successText,
            icon: "success",
          });
        } else {
          await Swal.fire({
            title: "Error!",
            text: res.message || `Failed to ${actionText}.`,
            icon: "error",
          });
        }
      } catch (error) {
        console.error(`Error ${actionText}:`, error);
        await Swal.fire({
          title: "Error!",
          text: `Failed to ${actionText}.`,
          icon: "error",
        });
      }
    });
  };

  if (!stateReport) return null;

  const { instructionsOnly, filingUrl } = stripUrlsFromInstructions(stateReport?.instructions);

  return (
    <>
      <GlobalStyles
        styles={{
          ".print-documents-section": {
            marginTop: "20px",
          },
          /* Force document table sharp corners on screen (first-image UI) */
          ".print-documents-section .print-document-table": {
            borderRadius: "0 !important",
            overflow: "hidden",
          },
          ".print-documents-section .print-document-table .MuiDataGrid-root": {
            borderRadius: "0 !important",
          },
          ".print-documents-section .print-document-table .MuiDataGrid-columnHeaders": {
            borderRadius: "0 !important",
          },
          ".print-documents-section .print-document-table .MuiDataGrid-columnHeader": {
            borderRadius: "0 !important",
            borderTopLeftRadius: "0 !important",
            borderTopRightRadius: "0 !important",
            borderBottomLeftRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
          },
          /* On screen: only DataGrid (3 columns) must show; hide print-only table */
          ".print-only-document-table": {
            position: "absolute !important",
            left: "-9999px !important",
            width: "0 !important",
            height: "0 !important",
            overflow: "hidden !important",
            visibility: "hidden !important",
            display: "none !important",
          },
          "@media print": {
            "body *": {
              visibility: "hidden !important",
            },
            ".MuiDialog-root, .MuiDialog-paper": {
              position: "static !important",
              margin: "0 !important",
              padding: "0 !important",
              width: "100% !important",
              maxWidth: "none !important",
              boxShadow: "none !important",
              borderRadius: "0 !important",
              border: "none !important",
              maxHeight: "none !important",
              height: "auto !important",
              visibility: "visible !important",
            },
            ".MuiDialog-paper *": {
              visibility: "visible !important",
            },
            ".MuiDialogTitle-root": {
              display: "block !important",
              position: "relative",
              backgroundColor: "#2099d2 !important",
              color: "#ffffff !important",
              padding: "8px 12px !important",
              textAlign: "center !important",
              fontWeight: "bold !important",
              fontSize: "14pt !important",
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
            },
            ".MuiDialogTitle-root *": {
              color: "#ffffff !important",
            },
            ".print-area": {
              position: "relative",
              width: "100% !important",
              margin: 0,
              padding: 0,
              backgroundColor: "white !important",
              fontSize: "10pt !important",
              lineHeight: "1.35 !important",
            },
            ".print-area *": {
              color: "#000000 !important",
            },
            // Keep Due Date red in print
            ".due-date-label, .due-date-value": {
              color: "#d32f2f !important",
            },
            ".MuiDialogContent-root": {
              padding: "0 !important",
              margin: "0 !important",
              overflow: "visible !important",
              maxHeight: "none !important",
            },
            ".MuiDialogActions-root": {
              display: "none !important",
            },
            "button, .MuiIconButton-root, .MuiCircularProgress-root": {
              display: "none !important",
            },
            ".hide-on-print": {
              display: "none !important",
            },
            "*": {
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
              colorAdjust: "exact !important",
            },
            // Preserve background colors
            "[style*='background-color: #E3F2FD'], [style*='background-color:#E3F2FD']": {
              backgroundColor: "#E3F2FD !important",
            },
            "[style*='background-color: yellow'], [style*='background-color:yellow']": {
              backgroundColor: "#FFFF00 !important",
            },
            ".print-area [style*='E3F2FD']": {
              padding: "6px 10px !important",
              marginBottom: "6px !important",
            },
            ".print-area [style*='yellow']": {
              padding: "6px 10px !important",
              marginBottom: "4px !important",
            },
            // Table styles
            ".MuiDataGrid-root": {
              borderCollapse: "collapse !important",
              width: "100% !important",
              fontSize: "11pt !important",
              tableLayout: "fixed !important",
            },
            ".MuiDataGrid-cell, .MuiDataGrid-columnHeader": {
              border: "none !important",
              padding: "10px 12px !important",
              color: "#000000 !important",
              fontSize: "11pt !important",
              wordWrap: "break-word !important",
              wordBreak: "break-word !important",
              overflow: "visible !important",
              whiteSpace: "normal !important",
              lineHeight: "1.4 !important",
              verticalAlign: "top !important",
            },
            // Ensure column headers and cells have matching widths
            ".MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
              boxSizing: "border-box !important",
            },
            // Type column - fixed width, left aligned (same for header and cell)
            ".MuiDataGrid-columnHeader[data-field='type']": {
              width: "250px !important",
              minWidth: "250px !important",
              maxWidth: "250px !important",
              textAlign: "left !important",
              color: "#ffffff !important",
            },
            ".MuiDataGrid-cell[data-field='type']": {
              width: "250px !important",
              minWidth: "250px !important",
              maxWidth: "250px !important",
              textAlign: "left !important",
              color: "#000000 !important",
            },
            // Document Name column - flexible width, left aligned (same for header and cell)
            ".MuiDataGrid-columnHeader[data-field='documentName']": {
              width: "auto !important",
              minWidth: "300px !important",
              flex: "1 1 0% !important",
              textAlign: "left !important",
              color: "#ffffff !important",
              fontWeight: "bold !important",
            },
            ".MuiDataGrid-cell[data-field='documentName']": {
              width: "auto !important",
              minWidth: "300px !important",
              flex: "1 1 0% !important",
              textAlign: "left !important",
              color: "#000000 !important",
              fontWeight: "normal !important",
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: "#0d4a63 !important",
              color: "#ffffff !important",
              display: "table-row !important",
            },
            ".MuiDataGrid-columnHeaders *": {
              color: "#ffffff !important",
            },
            ".MuiDataGrid-row": {
              display: "table-row !important",
              pageBreakInside: "avoid !important",
            },
            ".MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fafafa !important",
            },
            // Ensure all cell content is visible and properly aligned
            ".MuiDataGrid-cellContent": {
              overflow: "visible !important",
              textOverflow: "clip !important",
              whiteSpace: "normal !important",
              wordWrap: "break-word !important",
              color: "#000000 !important",
              textAlign: "left !important",
              width: "100% !important",
            },
            // Override Box alignment in cells for print
            ".MuiDataGrid-cell .MuiBox-root": {
              justifyContent: "flex-start !important",
              textAlign: "left !important",
              width: "100% !important",
            },
            // Ensure proper column header alignment
            ".MuiDataGrid-columnHeaderTitle": {
              textAlign: "left !important",
              fontWeight: "bold !important",
            },
            // Increase font size for Additional Notes and Instructions sections
            ".print-area strong": {
              fontSize: "13pt !important",
              fontWeight: "bold !important",
            },
            // Increase font size for content in Instructions and Additional Notes
            ".print-area > .MuiBox-root > .MuiBox-root": {
              fontSize: "13pt !important",
              lineHeight: "1.6 !important",
            },
            // Hide download column when printing
            ".MuiDataGrid-columnHeader[data-field='download'], .MuiDataGrid-cell[data-field='download']": {
              display: "none !important",
            },
            // Make sure table doesn't break across pages awkwardly
            ".MuiDataGrid-main": {
              pageBreakInside: "auto !important",
            },
            // Ensure proper table structure
            ".MuiDataGrid-virtualScroller": {
              overflow: "visible !important",
            },
            ".MuiDataGrid-virtualScrollerContent": {
              width: "100% !important",
            },
            // Fix column header alignment and ensure matching widths
            ".MuiDataGrid-columnHeader": {
              textAlign: "left !important",
              display: "table-cell !important",
            },
            ".MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "flex-start !important",
            },
            // Ensure cells align perfectly
            ".MuiDataGrid-cell": {
              display: "table-cell !important",
            },
            ".print-documents-section.no-documents": {
              display: "none !important",
            },
            ".print-documents-section.has-documents": {
              pageBreakBefore: "avoid !important",
              marginTop: "8px !important",
            },
            /* Compact content above Document Attachments so it fits on first page */
            ".print-area > .MuiBox-root": {
              marginBottom: "4px !important",
            },
            ".print-area .MuiGrid-root": {
              marginTop: "0 !important",
              marginBottom: "0 !important",
            },
            ".print-only-document-table": {
              display: "block !important",
              visibility: "visible !important",
              width: "100% !important",
              borderRadius: "0 !important",
            },
            ".print-only-document-table table": {
              width: "100% !important",
              backgroundColor: "#ffffff !important",
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
              borderRadius: "0 !important",
            },
            ".print-only-document-table thead th": {
              backgroundColor: "#0d4a63 !important",
              color: "#ffffff !important",
              borderRadius: "0 !important",
            },
            ".print-only-document-table td": {
              color: "#000000 !important",
            },
            ".print-wrapper.single-page": {
              pageBreakInside: "avoid !important",
              breakInside: "avoid !important",
              pageBreakBefore: "avoid !important",
              pageBreakAfter: "avoid !important",
              overflow: "hidden !important",
              maxHeight: "100vh !important",
              height: "auto !important",
              margin: "0 !important",
              padding: "0 !important",
            },
            ".print-wrapper.single-page *": {
              pageBreakInside: "avoid !important",
              breakInside: "avoid !important",
              pageBreakBefore: "avoid !important",
              pageBreakAfter: "avoid !important",
              marginBottom: "0 !important",
              paddingBottom: "0 !important",
            },
            "body, html": {
              margin: "0 !important",
              padding: "0 !important",
              height: "auto !important",
              overflow: "hidden !important",
            },
            ".MuiDialog-root, .MuiDialog-paper, .MuiDialogContent-root": {
              margin: "0 !important",
              padding: "0 !important",
              width: "100% !important",
              maxWidth: "100% !important",
              boxShadow: "none !important",
              borderRadius: "0 !important",
            },
            "@page": {
              size: "letter",
              margin: 0,
            },
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: "no-round-corner",
          sx: {
            width: "90%",
            maxWidth: 1200,
            minHeight: "50vh",
            maxHeight: "90vh",
            borderRadius: 2,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
            "@media print": {
              padding: "0 !important",
              maxHeight: "none",
              borderRadius: 0,
              boxShadow: "none",
            },
          },
        }}
        sx={{ "& .MuiDialog-paper": { overflow: "hidden" } }}
      >
        {loadingDocuments && <LinearProgress sx={{ flexShrink: 0 }} />}
        <DialogTitle
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 2,
            px: 3,
            background: "linear-gradient(135deg, #0d4a63 0%, #15607a 100%)",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            "@media print": {
              backgroundColor: "#2099d2 !important",
              background: "none",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssessmentOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
            <Typography
              component="span"
              variant="h6"
              sx={{ fontWeight: 600, letterSpacing: "0.02em" }}
            >
              {getStateReportPreviewTitle(
                stateReport?.additionalNotes,
                stateReport?.status,
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            className="hide-on-print"
            sx={{
              color: "rgba(255,255,255,0.9)",
              "&:hover": {
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            }}
            aria-label="Close dialog"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: 0,
            py: 0,
            overflow: "auto",
            "@media print": {
              padding: "0 !important",
            },
          }}
        >
          <Box
            className={`print-wrapper ${documents.length === 0 ? "single-page" : ""}`}
            sx={{
              p: 3,
              backgroundColor: "#f5f8fb",
              "@media print": {
                p: 0,
                backgroundColor: "#ffffff",
              },
            }}
          >
            <Box ref={dialogContentRef} className="print-area">
              <Grid
                container
                spacing={3}
                sx={{
                  "@media print": {
                    rowGap: 0,
                    columnGap: 0,
                  },
                }}
              >
                <NoticeReportSummaryCard
                  items={getNoticeSummaryItems(stateReport, states)}
                />
                <NoticeFilingDetailsCard
                  taxAmountFormatted={
                    stateReport?.taxAmount != null && String(stateReport.taxAmount).trim() !== ""
                      ? formatNoticeTaxAmount(stateReport.taxAmount)
                      : "N/A"
                  }
                  paymentItems={getNoticePaymentItems(stateReport)}
                />
                <NoticeInstructionsAndNotesCard
                  instructionsOnly={instructionsOnly}
                  filingUrl={filingUrl}
                  additionalNotes={getClientFacingAdditionalNotes(
                    stateReport?.additionalNotes,
                    stateReport?.notesToClient,
                  )}
                />

                <Grid item xs={12}>
                  <Box
                    className={`print-documents-section ${
                      documents.length === 0 ? "no-documents" : "has-documents"
                    }`}
                    sx={{ overflow: "visible" }}
                  >
                    <Typography variant="overline" sx={noticeSectionTitleSx}>
                      Document Attachments
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        ...noticeScreenCardSx,
                        p: 3,
                        "@media print": {
                          p: "4px 0",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Attached files for this state report
                        </Typography>
                        {documents.length > 0 && (
                          <Button
                            className="hide-on-print"
                            variant="outlined"
                            startIcon={
                              downloadingAll ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadIcon />
                              )
                            }
                            onClick={handleDownloadAll}
                            disabled={downloadingAll}
                            sx={{
                              borderColor: "#2099d2",
                              color: "#15607a",
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            {downloadingAll ? "Downloading..." : "Download All"}
                          </Button>
                        )}
                      </Box>

                      {loadingDocuments ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 160,
                          }}
                        >
                          <CircularProgress size={28} />
                        </Box>
                      ) : documents.length > 0 ? (
                        (() => {
                          const documentRows = documents.map((doc, index) => ({
                            id: doc.stateReportDocumentId || index,
                            type: doc.documentType || "N/A",
                            documentName: doc.documentName || "N/A",
                            blobAddress: doc.blobAddress || "",
                          }));

                          const columns: GridColDef[] = [
                            {
                              field: "download",
                              headerName: "Actions",
                              width: 180,
                              align: "center",
                              headerAlign: "center",
                              sortable: false,
                              filterable: false,
                              renderCell: (params) => (
                                <Box
                                  className="hide-on-print"
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    justifyContent: "center",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleViewDocument(
                                        params.row.blobAddress,
                                        params.row.documentName,
                                      )
                                    }
                                    disabled={!params.row.blobAddress}
                                    title="View"
                                    sx={{
                                      border: "1px solid #d9e4ec",
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#f4f8fb",
                                      },
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadDocument(params.row)}
                                    disabled={!params.row.blobAddress}
                                    title="Download"
                                    sx={{
                                      border: "1px solid #d9e4ec",
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#f4f8fb",
                                      },
                                    }}
                                  >
                                    <CloudDownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ),
                            },
                            {
                              field: "type",
                              headerName: "Type",
                              width: 250,
                              align: "left",
                              headerAlign: "left",
                              renderCell: (params) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    fontWeight: 500,
                                  }}
                                >
                                  {params.value}
                                </Box>
                              ),
                            },
                            {
                              field: "documentName",
                              headerName: "Document Name",
                              flex: 1,
                              minWidth: 300,
                              align: "left",
                              headerAlign: "left",
                              renderCell: (params) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  {params.value}
                                </Box>
                              ),
                            },
                          ];

                          return (
                            <Box sx={{ position: "relative", width: "100%", display: "block" }}>
                              <Box
                                className="print-only-document-table"
                                sx={{
                                  position: "absolute",
                                  left: "-9999px",
                                  top: 0,
                                  width: 0,
                                  height: 0,
                                  overflow: "hidden",
                                  visibility: "hidden",
                                  pointerEvents: "none",
                                  "@media print": {
                                    position: "static !important",
                                    left: "auto !important",
                                    width: "100% !important",
                                    height: "auto !important",
                                    overflow: "visible !important",
                                    visibility: "visible !important",
                                    pointerEvents: "auto !important",
                                    display: "block !important",
                                  },
                                }}
                              >
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "11pt",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <thead style={{ borderRadius: "0 !important" }}>
                                    <tr
                                      style={{
                                        backgroundColor: "#0d4a63",
                                        color: "#ffffff",
                                        borderRadius: "0 !important",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "5px 12px",
                                          textAlign: "left",
                                          border: "1px solid #083d52",
                                        }}
                                      >
                                        Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "5px 12px",
                                          textAlign: "left",
                                          border: "1px solid #083d52",
                                        }}
                                      >
                                        Document Name
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {documentRows.map((row, idx) => (
                                      <tr
                                        key={row.id}
                                        style={{
                                          backgroundColor: idx % 2 === 1 ? "#fafafa" : "#ffffff",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "5px 12px",
                                            border: "1px solid #e0e0e0",
                                            textAlign: "left",
                                            color: "#000",
                                          }}
                                        >
                                          {row.type}
                                        </td>
                                        <td
                                          style={{
                                            padding: "5px 12px",
                                            border: "1px solid #e0e0e0",
                                            textAlign: "left",
                                            color: "#000",
                                          }}
                                        >
                                          {row.documentName}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>
                              <Paper
                                className="print-document-table"
                                elevation={0}
                                sx={{
                                  width: "100%",
                                  border: "1px solid #d9e4ec",
                                  borderRadius: "16px !important",
                                  overflow: "hidden",
                                  "@media print": {
                                    display: "none !important",
                                  },
                                  "@media screen": {
                                    display: "block",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    height: 56 + documentRows.length * 52,
                                    width: "100%",
                                    "@media print": {
                                      height: "auto !important",
                                      overflow: "visible !important",
                                    },
                                  }}
                                >
                                  <DataGrid
                                    rows={documentRows}
                                    columns={columns}
                                    hideFooter
                                    disableColumnMenu
                                    disableSelectionOnClick
                                    autoHeight
                                    getRowHeight={() => 52}
                                    sx={{
                                      border: "none",
                                      "& .MuiDataGrid-cell": {
                                        borderBottom: "1px solid #e7eef4",
                                        padding: "10px 16px",
                                        display: "flex",
                                        alignItems: "center",
                                      },
                                      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                                        {
                                          outline: "none",
                                        },
                                      "& .MuiDataGrid-columnHeaders": {
                                        backgroundColor: "#0d4a63",
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                        borderBottom: "none",
                                        minHeight: "56px !important",
                                        maxHeight: "56px !important",
                                      },
                                      "& .MuiDataGrid-columnHeader": {
                                        padding: "10px 16px",
                                        minHeight: "56px !important",
                                        maxHeight: "56px !important",
                                      },
                                      "& .MuiDataGrid-columnHeaderTitle": {
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                      },
                                      "& .MuiDataGrid-row:nth-of-type(even)": {
                                        backgroundColor: "#fafcfe",
                                      },
                                      "& .MuiDataGrid-row:hover": {
                                        backgroundColor: "#f1f7fb",
                                      },
                                    }}
                                  />
                                </Box>
                              </Paper>
                            </Box>
                          );
                        })()
                      ) : (
                        <Box
                          sx={{
                            border: "1px dashed #c7d7e2",
                            borderRadius: 2,
                            backgroundColor: "#fbfdff",
                            py: 5,
                            px: 3,
                            textAlign: "center",
                            "@media print": {
                              border: "none",
                              backgroundColor: "transparent",
                              py: 1,
                              px: 0,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontStyle: "italic" }}
                          >
                            No documents available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </Grid>

                <NoticeStatusCard items={getNoticeStatusItems(stateReport)} />
              </Grid>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          className="hide-on-print"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "grey.50",
            px: 3,
            py: 2,
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<PrintOutlinedIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: "#2099d2",
              "&:hover": { backgroundColor: "#1a7ba8" },
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Print Page
          </Button>
          {(user?.userRoles.split(",").includes("Client Admin") ||
            user?.userRoles.split(",").includes("Client Accounting") ||
            user?.userRoles.split(",").includes("Customer User") ||
            user?.userRoles.split(",").includes("Client Processors") ||
            user?.userRoles.split(",").includes("State Reporter")) && (
            <Button
              variant="contained"
              startIcon={<DoneOutlinedIcon />}
              onClick={handleMarkAsDone}
              sx={{
                backgroundColor: selectedTab === 2 ? "#132D3D" : "#28a745",
                border: selectedTab === 2 ? "1px solid #28a745" : "none",
                color: "#fff",
                "&:hover": {
                  backgroundColor: selectedTab === 2 ? "#1a3d52" : "#218838",
                },
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {selectedTab === 2 ? "Return to Outstanding" : "Mark as Done"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <StateReportDocumentViewerModal
        open={documentViewerOpen}
        onClose={handleCloseDocumentViewer}
        fileName={documentViewerFileName}
        loading={loading}
        viewerUrl={documentViewerUrl}
      />
    </>
  );
};
